/* eslint-disable consistent-return */
import React, {
    ForwardedRef,
    JSX,
    Ref,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    Dimensions,
    FlatList,
    ListRenderItemInfo,
    Platform,
    View,
} from 'react-native';

import { ReduceMotion } from 'react-native-reanimated';
import { initialWindowMetrics } from 'react-native-safe-area-context';

import {
    BottomSheetFlatList,
    BottomSheetFlatListMethods,
    BottomSheetHandleProps,
    BottomSheetModal,
    useBottomSheetTimingConfigs,
} from '@gorhom/bottom-sheet';
import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';

import useHeaderHeight from 'utils/hooks/useHeaderHeight';
import { androidScreenMarginBottom, isTablet } from 'utils/responsiveness';

import BottomSheetHandle from 'components/BottomSheetHandle';
import BottomSheetModalBackdrop from 'components/BottomSheetModalBackdrop';

import styles from './styles';

type ListItem = {
    key: string;
    label: string;
    isSelected?: boolean;
};

type Props<T extends ListItem> = {
    data: T[];
    headerTitle?: string;
    rowHeight: number;
    renderRowItem(info: ListRenderItemInfo<T>): JSX.Element;
    keyExtractor(item: T): string;
    onClose(): void;
    onDismiss?(): void;
};

const { height: windowHeight } = Dimensions.get('window');
const List = Platform.OS === 'web' ? FlatList : BottomSheetFlatList;

const initialInsets = initialWindowMetrics?.insets || { bottom: 0, top: 0 };
const { bottom: bottomInset = 0, top: topInset = 0 } = Platform.select({
    default: { bottom: 0, top: 0 },
    android: { bottom: androidScreenMarginBottom ?? 0, top: initialInsets.top },
    ios: initialInsets,
});
const minListHeight = (windowHeight - topInset) / 2 + bottomInset;

function BottomSheetList<T extends ListItem>(
    {
        data,
        headerTitle,
        rowHeight,
        renderRowItem,
        keyExtractor,
        onClose,
        onDismiss,
    }: Props<T>,
    forwardedRef: ForwardedRef<BottomSheetModal | undefined>,
) {
    const prevSnapPoint = useRef(-1);
    const listRef = useRef<BottomSheetFlatListMethods>(undefined);

    const headerHeight = useHeaderHeight();
    const animationConfigs = useBottomSheetTimingConfigs({
        reduceMotion: ReduceMotion.Never,
        duration: 400,
    });

    const handleDismiss = useCallback(() => {
        onDismiss?.();
        onClose();
    }, [onClose, onDismiss]);

    const contentHeight = useMemo(
        () => data.length * rowHeight,
        [data.length, rowHeight],
    );

    const snapPoints = useMemo(() => {
        if (contentHeight < minListHeight) {
            return [contentHeight];
        }

        if (contentHeight + headerHeight < windowHeight - topInset) {
            return [minListHeight, contentHeight];
        }

        return [minListHeight, windowHeight];
    }, [contentHeight, headerHeight]);

    const [initialPositionConfig, setInitialPositionConfig] = useState<{
        snapPoint: number;
        itemIndex: null | number;
    }>({
        snapPoint: 0,
        itemIndex: null,
    });

    const defineInitialPosition = useCallback(() => {
        const itemIndex = data.findIndex((item) => item.isSelected);
        const selectedItemY = (itemIndex + 1) * rowHeight;

        if (minListHeight < selectedItemY) {
            setInitialPositionConfig({
                snapPoint: snapPoints.length - 1,
                itemIndex,
            });
        } else {
            setInitialPositionConfig({
                snapPoint: 0,
                itemIndex: null,
            });
        }
    }, [snapPoints.length, data, rowHeight]);

    useEffect(() => {
        setTimeout(defineInitialPosition, 500);
    }, [data, defineInitialPosition, snapPoints.length]);

    const renderHandleComponent = useCallback(
        ({ animatedIndex }: BottomSheetHandleProps) => {
            if (
                !isTablet &&
                snapPoints[snapPoints.length - 1] === windowHeight &&
                headerTitle?.length
            ) {
                return (
                    <BottomSheetHandle
                        title={headerTitle}
                        animatedIndex={animatedIndex}
                        onClosePress={onClose}
                    />
                );
            }

            return null;
        },
        [snapPoints, headerTitle, onClose],
    );

    const handleBottomSheetStateChange = useCallback(
        (snapPointIndex: number) => {
            const { itemIndex } = initialPositionConfig;

            if (
                prevSnapPoint.current === -1 &&
                snapPointIndex === 1 &&
                itemIndex
            ) {
                const offsetHeight =
                    headerHeight + topInset + (itemIndex + 1) * rowHeight;

                if (offsetHeight > windowHeight) {
                    setTimeout(() => {
                        listRef.current?.scrollToEnd();
                    }, 200);
                }
            }

            prevSnapPoint.current = snapPointIndex;
        },
        [initialPositionConfig, headerHeight],
    );

    return (
        <BottomSheetModal
            enablePanDownToClose
            ref={forwardedRef as Ref<BottomSheetModalMethods>}
            snapPoints={snapPoints}
            animationConfigs={animationConfigs}
            index={initialPositionConfig.snapPoint}
            handleComponent={renderHandleComponent}
            backdropComponent={BottomSheetModalBackdrop}
            backgroundStyle={styles.backgroundStyle}
            bottomInset={bottomInset}
            topInset={topInset}
            onDismiss={handleDismiss}
            onChange={handleBottomSheetStateChange}
        >
            <View style={styles.safeArea}>
                <List<T>
                    ref={listRef as Ref<BottomSheetFlatListMethods>}
                    style={styles.scroll}
                    data={data}
                    bounces={false}
                    keyExtractor={keyExtractor}
                    renderItem={renderRowItem}
                    persistentScrollbar
                    showsVerticalScrollIndicator
                />
            </View>
        </BottomSheetModal>
    );
}

export default React.forwardRef(BottomSheetList);
